import { useEffect, useState } from "react";
import { api, errMsg, ROLE_LABEL } from "@/lib/api";
import { EmptyState, PageHeader, Spinner } from "@/components/bits";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Copy, KeyRound, Loader2, Pencil, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client" });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [nameEdit, setNameEdit] = useState(null);
  const [newName, setNewName] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "client", active: true });
  const [provisional, setProvisional] = useState(null);
  const [resetting, setResetting] = useState(false);
  const isAdmin = user?.role === "tenant_admin";

  const load = () => api.get("/tenant/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tenant/users", form);
      toast.success("Usuário criado");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "client" });
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/tenant/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteUser = async () => {
    setDeleting(true);
    try {
      await api.delete(`/tenant/users/${deleteTarget.id}`);
      toast.success("Usuário excluído");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDeleting(false);
    }
  };

  const submitUserName = async (e) => {
    e.preventDefault();
    if (!newName.trim()) {
      toast.error("Informe o nome");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/tenant/users/${nameEdit.id}`, { name: newName.trim() });
      toast.success("Nome atualizado");
      setNameEdit(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({ name: u.name, email: u.email, role: u.role, active: !!u.active });
    setProvisional(null);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) {
      toast.error("Informe o nome");
      return;
    }
    const diff = {};
    if (editForm.name.trim() !== editTarget.name) diff.name = editForm.name.trim();
    if (editForm.email.trim().toLowerCase() !== editTarget.email) diff.email = editForm.email.trim();
    if (editForm.role !== editTarget.role) diff.role = editForm.role;
    if (editForm.active !== !!editTarget.active) diff.active = editForm.active;
    if (Object.keys(diff).length) {
      setSaving(true);
      try {
        await api.patch(`/tenant/users/${editTarget.id}`, diff);
        toast.success("Cadastro atualizado");
      } catch (err) {
        toast.error(errMsg(err));
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }
    setEditTarget(null);
    load();
  };

  const doResetPassword = async () => {
    setResetting(true);
    try {
      const { data } = await api.post(`/tenant/users/${editTarget.id}/reset-password`);
      setProvisional(data.provisional_password);
      toast.success("Senha provisória gerada");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setResetting(false);
    }
  };

  if (!users) return <Spinner />;

  return (
    <div data-testid="team-page">
      <PageHeader title="Equipe & Clientes" subtitle="Usuários da sua organização">
        {isAdmin && (
          <Button data-testid="new-user-button" onClick={() => setOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
            <Plus className="h-4 w-4 mr-1.5" /> Novo Usuário
          </Button>
        )}
      </PageHeader>

      {users.length === 0 ? (
        <EmptyState icon={Users} title="Nenhum usuário" subtitle="Cadastre atendentes e clientes da sua organização." />
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                <th className="px-4 py-3 text-xs font-medium text-zinc-500">Nome</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500">E-mail</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500">Papel</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500">Status</th>
                {isAdmin && <th className="px-4 py-3 text-xs font-medium text-zinc-500 text-right">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-zinc-800/60 hover:bg-zinc-800/50 transition-colors duration-200">
                  <td className="px-4 py-3 text-zinc-100 font-medium">
                    <span className="flex items-center gap-1.5">
                      {u.name}
                      {isAdmin && (
                        <button type="button" data-testid={`edit-name-${u.id}`} title="Alterar nome"
                          onClick={() => { setNameEdit(u); setNewName(u.name); }}
                          className="text-zinc-500 hover:text-indigo-400 transition-colors duration-200">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                  <td className="px-4 py-3 text-zinc-300">{ROLE_LABEL[u.role]}</td>
                  <td className="px-4 py-3">
                    <Badge className={`border-0 text-[11px] hover:bg-transparent ${u.active ? "text-emerald-400 bg-emerald-400/10" : "text-zinc-400 bg-zinc-400/10"}`}>
                      {u.active ? "Ativo" : "Inativo"}
                    </Badge>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      {u.id !== user.id && (
                        <span className="inline-flex items-center gap-1">
                          <Button data-testid={`toggle-user-${u.id}`} variant="ghost" size="sm" onClick={() => toggleActive(u)}
                            className={`text-xs ${u.active ? "text-zinc-400 hover:text-rose-400" : "text-zinc-400 hover:text-emerald-400"} hover:bg-zinc-800`}>
                            {u.active ? "Desativar" : "Reativar"}
                          </Button>
                          <Button data-testid={`edit-user-${u.id}`} variant="ghost" size="icon" title="Editar cadastro"
                            onClick={() => openEdit(u)}
                            className="h-7 w-7 text-zinc-500 hover:text-indigo-400 hover:bg-zinc-800">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button data-testid={`delete-user-${u.id}`} variant="ghost" size="icon" title="Excluir usuário"
                            onClick={() => setDeleteTarget(u)}
                            className="h-7 w-7 text-zinc-500 hover:text-rose-400 hover:bg-zinc-800">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Novo usuário</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">Cadastre atendentes, gestores ou clientes da sua organização.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="user-create-form">
            <div>
              <Label className="text-xs text-zinc-400">Nome</Label>
              <Input data-testid="user-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">E-mail</Label>
              <Input data-testid="user-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Senha inicial</Label>
              <Input data-testid="user-password-input" type="password" required minLength={6} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Papel</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-200 rounded-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                  <SelectItem value="client">Cliente (portal de chamados)</SelectItem>
                  <SelectItem value="agent">Atendente</SelectItem>
                  <SelectItem value="tenant_admin">Gestor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="user-create-submit" type="submit" disabled={saving}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-md">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar usuário"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Excluir usuário</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Tem certeza que deseja excluir <span className="text-zinc-200 font-medium">{deleteTarget?.name}</span> ({deleteTarget?.email})? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2" data-testid="delete-user-dialog">
            <Button data-testid="delete-user-cancel" variant="ghost" onClick={() => setDeleteTarget(null)}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
              Cancelar
            </Button>
            <Button data-testid="delete-user-confirm" onClick={deleteUser} disabled={deleting}
              className="bg-rose-600 hover:bg-rose-500 text-white rounded-md transition-colors duration-200">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!nameEdit} onOpenChange={(v) => !v && setNameEdit(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Alterar nome</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Usuário: <span className="text-zinc-200">{nameEdit?.email}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitUserName} className="space-y-4" data-testid="edit-name-form">
            <div>
              <Label className="text-xs text-zinc-400">Nome</Label>
              <Input data-testid="edit-name-input" required value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
            </div>
            <Button data-testid="edit-name-submit" type="submit" disabled={saving}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar nome"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Editar cadastro</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Altere os dados do membro da equipe ou gere uma senha provisória.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4" data-testid="edit-user-form">
            <div>
              <Label className="text-xs text-zinc-400">Nome</Label>
              <Input data-testid="edit-user-name" required value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">E-mail</Label>
              <Input data-testid="edit-user-email" type="email" required value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-xs text-zinc-400">Papel</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="edit-user-role" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-200 rounded-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                    <SelectItem data-testid="edit-user-role-client" value="client">Cliente</SelectItem>
                    <SelectItem data-testid="edit-user-role-agent" value="agent">Atendente</SelectItem>
                    <SelectItem data-testid="edit-user-role-admin" value="tenant_admin">Gestor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer pb-2" data-testid="edit-user-active-label">
                <Checkbox data-testid="edit-user-active" checked={editForm.active}
                  onCheckedChange={(v) => setEditForm({ ...editForm, active: !!v })} />
                Usuário ativo
              </label>
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 space-y-2" data-testid="edit-user-password-section">
              <p className="text-xs font-medium text-zinc-400">Senha de acesso</p>
              {provisional ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <code data-testid="edit-user-provisional"
                      className="flex-1 text-sm font-mono bg-amber-400/10 text-amber-300 rounded-md px-3 py-2 select-all">{provisional}</code>
                    <Button type="button" data-testid="edit-user-copy-password" variant="ghost" size="icon" title="Copiar senha provisória"
                      onClick={() => { navigator.clipboard?.writeText(provisional); toast.success("Senha copiada"); }}
                      className="h-9 w-9 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-[11px] text-amber-400/90" data-testid="edit-user-provisional-note">
                    Anote e informe ao usuário — ela não será exibida novamente. No próximo login, ele será obrigado a definir uma nova senha pessoal.
                  </p>
                </div>
              ) : (
                <Button type="button" data-testid="edit-user-reset-password" variant="outline" disabled={resetting}
                  onClick={doResetPassword}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 rounded-md">
                  {resetting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <KeyRound className="h-4 w-4 mr-1.5" />}
                  Resetar senha (gera provisória)
                </Button>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" data-testid="edit-user-cancel" variant="ghost" onClick={() => setEditTarget(null)}
                className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
                {provisional ? "Fechar" : "Cancelar"}
              </Button>
              <Button data-testid="edit-user-submit" type="submit" disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar alterações"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
